/**
 * StagePill — Figma component set `Pill/Stage` (node 6:34, 9 variants).
 *
 * Measured: px-10 py-4, `radius/pill`, a 6px dot, 6px gap, `Label/12` (12/16
 * weight 500). Colours come from the tone map in `domain/stages.ts`; NO colour
 * and NO label is named here — that is the point of the single stage map.
 *
 * ASSET NOTE (design-to-code G5). The 6px dot is an exported `Ellipse` asset in
 * Figma. It is rendered as a CSS circle instead, and that is a correctness
 * decision rather than a shortcut: the exported bitmap bakes in the pill's
 * ORIGINAL foreground hex, and two of those hexes failed WCAG AA and were nudged
 * (`tokens.css`, nudges ① and ②). Using the asset would silently reintroduce the
 * exact colour the AA fix removed. `currentColor` keeps the dot on the token.
 */

import { stagePillLabel, stageToneClasses, STAGES } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import { cn } from '../../lib/cn';

export interface StagePillProps {
  stage: InquiryStage;
  className?: string;
}

export function StagePill({ stage, className }: StagePillProps): React.JSX.Element {
  const descriptor = STAGES[stage];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        stageToneClasses(descriptor.tone),
        className,
      )}
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      {stagePillLabel(stage)}
    </span>
  );
}
